import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, MapPin, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  {
    id: "amina",
    name: "Amina, 32",
    location: "Dar es Salaam",
    quote:
      "When I felt invisible, this community saw me. The mentorship helped me reopen my tailoring shop and feed my children with dignity.",
    program: "Economic Empowerment",
    image:
      "https://images.pexels.com/photos/6281959/pexels-photo-6281959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "njeri",
    name: "Njeri, 27",
    location: "Mwanza",
    quote:
      "Raising my son with cerebral palsy alone was crushing. The peer circle and therapy referrals gave me my breath back.",
    program: "Children with Disabilities",
    image:
      "https://images.pexels.com/photos/27499880/pexels-photo-27499880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function StoriesTeaser() {
  const [stories, setStories] = useState(FALLBACK);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/stories`)
      .then((r) => {
        if (mounted && Array.isArray(r.data) && r.data.length)
          setStories(r.data.slice(0, 2));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="stories"
      data-testid="stories-teaser-section"
      className="relative py-20 lg:py-28 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              Stories of strength
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
              In her own{" "}
              <span className="italic text-brand-forest">words.</span>
            </h2>
          </div>
          <Link
            to="/stories"
            data-testid="stories-see-all-link"
            className="btn-outline self-start lg:self-end"
          >
            See all stories
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
          {stories.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`story-teaser-${s.id}`}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group rounded-[28px] overflow-hidden bg-white ring-1 ring-brand-border hover-lift lg:flex"
            >
              <div className="relative h-64 lg:h-auto lg:w-1/2 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="font-serif text-xl">{s.name}</div>
                  <div className="text-[11px] mt-1 flex items-center gap-1.5 text-white/85">
                    <MapPin className="h-3 w-3" />
                    {s.location} · {s.program}
                  </div>
                </div>
              </div>
              <div className="p-6 lg:w-1/2 lg:flex lg:flex-col lg:justify-center">
                <Quote className="h-6 w-6 text-brand-gold" />
                <p className="mt-3 font-serif italic text-lg leading-snug text-brand-ink">
                  “{s.quote}”
                </p>
                <Link
                  to="/stories"
                  data-testid={`read-more-${s.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest underline-link"
                >
                  Read more stories
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
